import React, { useState, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import "./courseGuide.css";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";

const CourseGuide = ({ clubData, setSelectedHole, selectedHole }) => {
  const [currentHoleIndex, setCurrentHoleIndex] = useState(0);
  const [isFullpageSponsorVisible, setIsFullpageSponsorVisible] =
    useState(true);
  const [isSponsorBannerVisible, setIsSponsorBannerVisible] = useState(false);
  const [sponsorBannerClass, setSponsorBannerClass] = useState("");
  const { t } = useTranslation();

  // sets the current hole index to 0 and the selected hole to 1 when the club data changes
  useEffect(() => {
    setCurrentHoleIndex(0);
    if (clubData && clubData.holes && clubData.holes.length > 0) {
      setSelectedHole(1); // Start with the first hole
    }
  }, [clubData, setSelectedHole]);

  useEffect(() => {
    if (selectedHole && clubData && clubData.holes) {
      setCurrentHoleIndex(selectedHole - 1);
    }
  }, [selectedHole, clubData]);

  // useEffect that sets the sponsorBannerClass to slide-out after 7 seconds to control the sponsor banner animation
  useEffect(() => {
    setSponsorBannerClass(""); // Reset the class

    if (!isFullpageSponsorVisible) {
      const timer = setTimeout(() => {
        setSponsorBannerClass("slide-out");
      }, 7000); // 7 seconds

      return () => clearTimeout(timer); // Clear the timer on cleanup
    }
  }, [currentHoleIndex, clubData.holes, isFullpageSponsorVisible]);

  useEffect(() => {
    const holeData = clubData.holes[currentHoleIndex];
    if (holeData === undefined) return;
    const fullpageSponsor = holeData.sponsors.find(
      (sponsor) => sponsor.placement === "fullpage-centered-first"
    );
    setIsFullpageSponsorVisible(!!fullpageSponsor);
  }, [currentHoleIndex, clubData.holes]);

  // remove any unsafe content like scripts and event handlers from the HTML
  const createMarkup = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };

  const handleCloseBanner = () => {
    setSponsorBannerClass("slide-out");
  };

  const displayHole = useCallback(() => {
    if (
      !clubData ||
      !clubData.holes ||
      clubData.holes.length === 0 ||
      currentHoleIndex >= clubData.holes.length
    )
      return (
        // Display a placeholder if there is no data to display. Makes sure the content doesn't jump around.
        <section id="glfr-course-guide-container" className="placeholder">
          <h1 className="glfr-cg-title">Bane Guides</h1>
          <div className="glfr-cg-hole-image-placeholder"></div>
          <div className="glfr-cg-content">
            <div className="glfr-cg-hole-details">
              <div className="glfr-cg-tees-placeholder"></div>
              <div className="glfr-cg-change-hole">
                <button className="glfr-cg-prev-btn" disabled>
                  Prev
                </button>
                <div className="glfr-cg-hole-info">
                  <div className="glfr-cg-hole-number">Loading...</div>
                  <div className="glfr-cg-par&handicap">
                    <div className="glfr-cg-par">Par: --</div>
                    <div className="glfr-cg-handicap">Idx: --</div>
                  </div>
                </div>
                <button className="glfr-cg-next-btn" disabled>
                  Next
                </button>
              </div>
            </div>
          </div>
        </section>
      );

    const holeData = clubData.holes[currentHoleIndex];

    //check if there is a sponsor image where placement = fullpage-centered-first
    let fullpageSponsor = holeData.sponsors.find(
      (sponsor) => sponsor.placement === "fullpage-centered-first"
    );
    return (
      <section id="glfr-course-guide-container">
        <div className="cg-sponsors">
          {fullpageSponsor && isFullpageSponsorVisible ? (
            <div className="fullpageSponsor">
              <button
                className="close-btn"
                onClick={() => {
                  setIsFullpageSponsorVisible(false);
                  setIsSponsorBannerVisible(true);
                }}
              >
                X
              </button>
              <img src={fullpageSponsor.image} alt={fullpageSponsor.name} />
            </div>
          ) : null}
          {holeData.sponsors.length > 0 && isSponsorBannerVisible ? (
            <div className={`sponsor-banner ${sponsorBannerClass}`}>
              {" "}
              <button className="close-btn" onClick={handleCloseBanner}>
                X
              </button>
              <img
                className="sponsor-first"
                src={holeData.sponsors[0].image}
                alt={holeData.sponsors[0].name}
              />
            </div>
          ) : null}
        </div>
        <div className="cg-Content">
          <div className="glfr-cg-hole-info">
            <div className="cg-Guideline">
              {holeData.guideline !== "" ? (
                <>
                  <HelpOutlineIcon />
                  <span
                    className="Guideline"
                    dangerouslySetInnerHTML={createMarkup(holeData.guideline)}
                  />
                </>
              ) : null}
            </div>
            <div className="glfr-cg-par">{t('par')} {holeData.tees[0].par}</div>
            <div className="glfr-cg-hole-number">{selectedHole}</div>
            <div className="glfr-cg-handicap">
              {t('hcp')} {holeData.tees[0].handicap}
            </div>
          </div>
          <div className="cg-Image">
            <img src={holeData.holeImage} alt={holeData.holeImage} />
            <div className="cg-Image-Controls">
              <button
                className="glfr-cg-prev-btn"
                onClick={() => {
                  const newIndex = currentHoleIndex === 0 ? clubData.holes.length - 1 : currentHoleIndex - 1
                  setCurrentHoleIndex(newIndex);
                  setSelectedHole(newIndex + 1);
                }}
              >
                <ChevronLeftIcon fontSize="large"/>
              </button>
              <button
                className="glfr-cg-next-btn"
                onClick={() => {
                  const newIndex = currentHoleIndex === clubData.holes.length - 1 ? 0 : currentHoleIndex + 1
                  setCurrentHoleIndex(newIndex);
                  setSelectedHole(newIndex + 1);
                }}
              >
                <ChevronRightIcon fontSize="large"/>
              </button>
            </div>
          </div>
          <br />
        </div>
      </section>
    );
  }, [
    clubData,
    currentHoleIndex,
    setSelectedHole,
    isFullpageSponsorVisible,
    isSponsorBannerVisible,
    sponsorBannerClass,
    selectedHole,
    t
  ]);

  return displayHole();
};

export default CourseGuide;
